// src/components/FileSizeErrorModal.js
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  Image,
} from 'react-native';
import { useTranslation } from '../i18n';

const COLORS = {
  primary: "#14B8A6",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#111827",
  textMuted: "#6B7280",
  error: "#DC2626",
  errorLight: "#FEF2F2",
  border: "#E5E7EB",
};

export default function FileSizeErrorModal({
  visible,
  onDismiss,
  errorMessage,
  fileName,
  fileSize,
  onRetry,
}) {
  const { t } = useTranslation();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          {/* Error Icon */}
          <View style={styles.iconContainer}>
            <View style={styles.errorCircle}>
              <Text style={styles.errorIcon}>⚠️</Text>
            </View>
          </View>

          {/* Title */}
          <Text style={styles.modalTitle}>
            {t('fileValidation.errorTitle', 'Fichier trop volumineux')}
          </Text>

          {/* File Info */}
          {fileName && (
            <View style={styles.fileInfoBox}>
              <Text style={styles.fileNameLabel}>
                {t('fileValidation.fileName', 'Fichier')}:
              </Text>
              <Text style={styles.fileName}>{fileName}</Text>
              {fileSize && (
                <Text style={styles.fileSize}>
                  {t('fileValidation.fileSize', 'Taille')}: {fileSize}
                </Text>
              )}
            </View>
          )}

          {/* Error Message */}
          <Text style={styles.errorMessage}>
            {errorMessage || t('fileValidation.fileTooLargeGeneric', 'Le fichier sélectionné est trop volumineux. La taille maximale autorisée est de 15MB.')}
          </Text>

          {/* Help Text */}
          <View style={styles.helpBox}>
            <Text style={styles.helpTitle}>
              {t('fileValidation.helpTitle', '📌 Solutions')}
            </Text>
            <Text style={styles.helpText}>
              • {t('fileValidation.solution1', 'Compressez le fichier avant de le télécharger')}
            </Text>
            <Text style={styles.helpText}>
              • {t('fileValidation.solution2', 'Prenez une photo avec une résolution plus faible')}
            </Text>
            <Text style={styles.helpText}>
              • {t('fileValidation.solution3', 'Utilisez un fichier PDF de taille réduite')}
            </Text>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <Pressable
              style={({ pressed }) => [
                styles.retryButton,
                pressed && styles.buttonPressed,
              ]}
              onPress={onRetry}
            >
              <Text style={styles.retryButtonText}>
                {t('fileValidation.chooseAnother', 'Choisir un autre fichier')}
              </Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.cancelButton,
                pressed && styles.buttonPressed,
              ]}
              onPress={onDismiss}
            >
              <Text style={styles.cancelButtonText}>
                {t('common.cancel', 'Annuler')}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(17, 24, 39, 0.75)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalCard: {
    backgroundColor: COLORS.card,
    borderRadius: 24,
    padding: 24,
    width: "100%",
    maxWidth: 500,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  iconContainer: {
    alignItems: "center",
    marginBottom: 16,
  },
  errorCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: COLORS.errorLight,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.error,
  },
  errorIcon: {
    fontSize: 32,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 16,
  },
  fileInfoBox: {
    backgroundColor: COLORS.background,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  fileNameLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: COLORS.textMuted,
    marginBottom: 4,
  },
  fileName: {
    fontSize: 15,
    fontWeight: "500",
    color: COLORS.textDark,
    marginBottom: 4,
  },
  fileSize: {
    fontSize: 14,
    color: COLORS.error,
    fontWeight: "600",
  },
  errorMessage: {
    fontSize: 15,
    color: COLORS.error,
    textAlign: "center",
    marginBottom: 16,
    lineHeight: 22,
    fontWeight: "500",
  },
  helpBox: {
    backgroundColor: "#FEFCE8",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: "#FDE047",
  },
  helpTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#854D0E",
    marginBottom: 8,
  },
  helpText: {
    fontSize: 13,
    color: "#854D0E",
    marginBottom: 4,
    lineHeight: 18,
  },
  buttonContainer: {
    gap: 12,
  },
  retryButton: {
    backgroundColor: COLORS.primary,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "700",
  },
  cancelButton: {
    backgroundColor: "#F3F4F6",
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  cancelButtonText: {
    color: COLORS.textDark,
    fontSize: 16,
    fontWeight: "600",
  },
  buttonPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
});